import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { theme, alpha } from '../../../theme';
import { useBookingGrid } from '../../../hooks/venue/useBookingGrid';
import { useOwnerComplex } from '../../../hooks/venue/useOwnerComplex';
import BookingGrid from '../../../components/venue/owner/BookingGrid';
import BookingGridControls from '../../../components/venue/owner/BookingGridControls';
import BookingDetailModal from '../../../components/venue/owner/BookingDetailModal';
import CreateManualBookingModal from '../../../components/venue/owner/CreateManualBookingModal';
import QuickCreatePopover from '../../../components/venue/owner/QuickCreatePopover';
import { GridBooking, CreateManualBookingRequest, BOOKING_COLORS, BookingChannel } from '../../../types/venue';
import { Calendar, Plus, RefreshCw, CheckCircle } from 'lucide-react';

const ReservationGridPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSetupBanner, setShowSetupBanner] = useState(
    new URLSearchParams(location.search).get('setup') === 'complete'
  );
  const {
    gridData,
    loading,
    error,
    date,
    view,
    selectedVenueId,
    setDate,
    setView,
    setSelectedVenueId,
    fetchGridData,
    goToToday,
    goToPrevious,
    goToNext,
    createManualBooking,
    quickEditBooking,
    cancelBooking,
    venues,
    allVenues,
    bookings,
    fixedBookings,
    dateRange,
  } = useBookingGrid({ autoRefresh: true, refreshInterval: 60000 });

  const { complex, fetchComplex } = useOwnerComplex();

  // Merge fixed bookings into grid as pseudo-GridBooking objects
  const mergedBookings = useMemo(() => {
    if (!fixedBookings || fixedBookings.length === 0) return bookings;

    const pseudoBookings: GridBooking[] = [];

    const createPseudoBooking = (fb: typeof fixedBookings[0], targetDate: string, idSuffix?: string) => ({
      id: `fixed-${fb.id}${idSuffix ? `-${idSuffix}` : ''}`,
      venueId: fb.venueId,
      bookingDate: targetDate,
      startTime: fb.startTime,
      endTime: fb.endTime,
      bookingType: 'FIXED' as const,
      color: BOOKING_COLORS.FIXED,
      customerName: fb.customerName,
      status: 'CONFIRMED' as const,
      totalAmount: fb.agreedPrice,
      durationHours: 1,
      depositPaid: 0,
      serviceFee: 0,
      confirmationCode: '',
      bookerId: '',
      createdAt: '',
    } as GridBooking);

    if (view === 'daily') {
      const dayOfWeek = new Date(date + 'T00:00:00').getDay();
      fixedBookings
        .filter(fb => fb.dayOfWeek === dayOfWeek)
        .forEach(fb => {
          const exists = bookings.some(
            b => b.venueId === fb.venueId && b.bookingDate === date && b.startTime === fb.startTime && b.status !== 'CANCELLED'
          );
          if (!exists) {
            pseudoBookings.push(createPseudoBooking(fb, date));
          }
        });
    } else {
      const startDate = new Date(dateRange.start + 'T00:00:00');
      for (let i = 0; i < 7; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        const dayDate = d.toISOString().split('T')[0];
        const dayOfWeek = d.getDay();

        fixedBookings
          .filter(fb => fb.dayOfWeek === dayOfWeek)
          .forEach(fb => {
            const exists = bookings.some(
              b => b.venueId === fb.venueId && b.bookingDate === dayDate && b.startTime === fb.startTime && b.status !== 'CANCELLED'
            );
            if (!exists) {
              pseudoBookings.push(createPseudoBooking(fb, dayDate, dayDate));
            }
          });
      }
    }

    return [...bookings, ...pseudoBookings];
  }, [fixedBookings, bookings, date, dateRange, view]);

  // Modal states
  const [selectedBooking, setSelectedBooking] = useState<GridBooking | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createModalDefaults, setCreateModalDefaults] = useState<{
    venueId?: string;
    date?: string;
    time?: string;
    customerName?: string;
    customerPhone?: string;
    notes?: string;
    bookingChannel?: BookingChannel;
  }>({});

  // Quick-create popover state
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);
  const [quickCreateAnchorRect, setQuickCreateAnchorRect] = useState<DOMRect | null>(null);
  const [quickCreateContext, setQuickCreateContext] = useState<{
    venueId: string;
    date: string;
    time: string;
  } | null>(null);

  useEffect(() => {
    fetchComplex();
  }, [fetchComplex]);

  // Pre-select venue if navigated from CreateVenuePage
  useEffect(() => {
    const state = location.state as { selectedVenueId?: string } | null;
    if (state?.selectedVenueId) {
      setSelectedVenueId(state.selectedVenueId);
    }
  }, [location.state, setSelectedVenueId]);

  // When switching to weekly, force a venue selection
  const handleViewChange = (newView: 'daily' | 'weekly') => {
    if (newView === 'weekly' && !selectedVenueId) {
      const venueList = allVenues.length > 0 ? allVenues : venues;
      if (venueList.length > 0) {
        setSelectedVenueId(venueList[0].id);
      }
    }
    setView(newView);
  };

  // Handlers
  const handleSlotClick = (venueId: string, slotDate: string, time: string, cellRect: DOMRect) => {
    if (window.innerWidth <= 768) {
      // Mobile: open full modal directly
      setCreateModalDefaults({ venueId, date: slotDate, time });
      setIsCreateModalOpen(true);
    } else {
      // Desktop: open quick-create popover
      setQuickCreateAnchorRect(cellRect);
      setQuickCreateContext({ venueId, date: slotDate, time });
      setIsQuickCreateOpen(true);
    }
  };

  const handleOpenFullModal = (partialData: { customerName: string; customerPhone: string; notes: string; bookingChannel?: BookingChannel }) => {
    setIsQuickCreateOpen(false);
    if (quickCreateContext) {
      setCreateModalDefaults({
        venueId: quickCreateContext.venueId,
        date: quickCreateContext.date,
        time: quickCreateContext.time,
        customerName: partialData.customerName || undefined,
        customerPhone: partialData.customerPhone || undefined,
        notes: partialData.notes || undefined,
        bookingChannel: partialData.bookingChannel,
      });
      setIsCreateModalOpen(true);
    }
  };

  const handleBookingClick = (booking: GridBooking) => {
    setSelectedBooking(booking);
    setIsDetailModalOpen(true);
  };

  const handleCreateBooking = async (data: CreateManualBookingRequest) => {
    const result = await createManualBooking(data);
    return result;
  };

  const handleQuickEdit = async (bookingId: string, data: any) => {
    const result = await quickEditBooking(bookingId, data);
    if (result.success) {
      // Update the selected booking if it's being edited
      if (selectedBooking?.id === bookingId && result.booking) {
        setSelectedBooking(result.booking);
      }
    }
    return result;
  };

  const handleCancelBooking = async (bookingId: string) => {
    const result = await cancelBooking(bookingId);
    return result;
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    padding: '32px',
    maxWidth: '1600px',
    margin: '0 auto',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const titleRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    letterSpacing: theme.fonts.letterSpacingWide,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
  };

  const headerActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 24px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: theme.colors.primary,
    color: '#000',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.surface,
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
  };

  const loadingStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    fontSize: theme.fonts.sizeSubtitle,
    color: theme.colors.textSecondary,
  };

  const errorStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `2px solid ${theme.colors.danger}`,
    color: theme.colors.danger,
    marginBottom: '24px',
  };

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
  };

  const emptyIconStyle: React.CSSProperties = {
    marginBottom: '16px',
    color: theme.colors.textSecondary,
  };

  const emptyTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
  };

  const emptyMessageStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
    marginBottom: '24px',
  };

  const statsBarStyle: React.CSSProperties = {
    display: 'flex',
    gap: '24px',
    padding: '16px 24px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: '16px',
  };

  const statItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const statLabelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const statValueStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  // Calculate stats for current view
  const todayBookings = bookings.filter(
    (b) => b.bookingDate === date && b.status !== 'CANCELLED'
  );
  const totalRevenue = todayBookings.reduce((sum, b) => sum + b.totalAmount, 0);
  const confirmedBookings = todayBookings.filter(
    (b) => b.status === 'CONFIRMED' || b.status === 'COMPLETED'
  );
  const pendingBookings = todayBookings.filter((b) => b.status === 'PENDING');

  if (loading && venues.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>Cargando grilla de reservas...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={titleRowStyle}>
          <div>
            <h1 style={titleStyle}>GRILLA DE RESERVAS</h1>
            <p style={subtitleStyle}>
              {complex?.name || 'Mi Complejo'} - Gestiona todas tus reservas en un solo lugar
            </p>
          </div>
          <div style={headerActionsStyle}>
            <button
              style={secondaryButtonStyle}
              onClick={() => fetchGridData()}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.background;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.surface;
              }}
            >
              <RefreshCw size={18} />
              Actualizar
            </button>
            <button
              data-testid="manual-booking-btn"
              style={buttonStyle}
              onClick={() => {
                setCreateModalDefaults({});
                setIsCreateModalOpen(true);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#00D96F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primary;
              }}
            >
              <Plus size={20} />
              Nueva Reserva
            </button>
          </div>
        </div>
      </div>

      {/* Banner de configuración exitosa — aparece al terminar el onboarding */}
      {showSetupBanner && (
        <div style={{
          backgroundColor: `${alpha('primary', '15')}`,
          border: `2px solid ${alpha('primary', '50')}`,
          borderRadius: theme.borderRadius.card,
          padding: '16px 20px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <CheckCircle size={24} color={theme.colors.primary} />
            <div>
              <div style={{ fontWeight: theme.fonts.weightBold, color: theme.colors.text, fontSize: theme.fonts.sizeBody }}>
                ¡Tu cancha está lista!
              </div>
              <div style={{ color: theme.colors.textSecondary, fontSize: theme.fonts.sizeSmall }}>
                Ya podés empezar a recibir reservas. Cargá fotos de tu cancha para atraer más jugadores.
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowSetupBanner(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textSecondary, fontSize: '1.2rem', lineHeight: 1 }}
          >
            ×
          </button>
        </div>
      )}

      {error && <div style={errorStyle}>{error}</div>}

      {venues.length === 0 ? (
        <div style={emptyStateStyle}>
          <div style={emptyIconStyle}>
            <Calendar size={64} />
          </div>
          <div style={emptyTitleStyle}>No tenés canchas configuradas</div>
          <div style={emptyMessageStyle}>
            Creá tu primera cancha para empezar a gestionar reservas
          </div>
          <button
            onClick={() => navigate('/owner/onboarding')}
            style={{
              padding: '12px 24px',
              backgroundColor: theme.colors.primary,
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Plus size={18} />
            Crear mi primera cancha
          </button>
        </div>
      ) : (
        <>
          {/* Stats Bar */}
          <div style={statsBarStyle}>
            <div style={statItemStyle}>
              <span style={statLabelStyle}>Reservas del día:</span>
              <span style={statValueStyle}>{todayBookings.length}</span>
            </div>
            <div style={statItemStyle}>
              <span style={statLabelStyle}>Confirmadas:</span>
              <span style={{ ...statValueStyle, color: theme.colors.primary }}>
                {confirmedBookings.length}
              </span>
            </div>
            <div style={statItemStyle}>
              <span style={statLabelStyle}>Pendientes:</span>
              <span style={{ ...statValueStyle, color: theme.colors.warning }}>
                {pendingBookings.length}
              </span>
            </div>
            <div style={statItemStyle}>
              <span style={statLabelStyle}>Ingresos del día:</span>
              <span style={{ ...statValueStyle, color: theme.colors.success }}>
                ${totalRevenue.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>

          {/* Controls */}
          <BookingGridControls
            date={date}
            view={view}
            venues={(allVenues.length > 0 ? allVenues : venues).map((v) => ({ id: v.id, name: v.name }))}
            selectedVenueId={selectedVenueId}
            onDateChange={setDate}
            onViewChange={handleViewChange}
            onVenueChange={setSelectedVenueId}
            onGoToToday={goToToday}
            onGoToPrevious={goToPrevious}
            onGoToNext={goToNext}
          />

          {/* Grid */}
          <BookingGrid
            venues={venues}
            bookings={mergedBookings}
            date={date}
            dateRange={dateRange}
            view={view}
            selectedVenueId={selectedVenueId}
            startHour={7}
            endHour={24}
            slotDuration={60}
            onSlotClick={handleSlotClick}
            onBookingClick={handleBookingClick}
          />
        </>
      )}

      {/* Detail Modal */}
      <BookingDetailModal
        isOpen={isDetailModalOpen}
        booking={selectedBooking}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedBooking(null);
        }}
        onQuickEdit={handleQuickEdit}
        onCancel={handleCancelBooking}
      />

      {/* Quick-Create Popover (desktop only) */}
      {quickCreateContext && (
        <QuickCreatePopover
          isOpen={isQuickCreateOpen}
          onClose={() => setIsQuickCreateOpen(false)}
          onSubmit={handleCreateBooking}
          onOpenFullModal={handleOpenFullModal}
          anchorRect={quickCreateAnchorRect}
          venueId={quickCreateContext.venueId}
          venueName={venues.find((v) => v.id === quickCreateContext.venueId)?.name || 'Cancha'}
          date={quickCreateContext.date}
          time={quickCreateContext.time}
          depositAmount={venues.find((v) => v.id === quickCreateContext.venueId)?.depositAmount}
        />
      )}

      {/* Create Modal */}
      <CreateManualBookingModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setCreateModalDefaults({});
        }}
        onSubmit={handleCreateBooking}
        venues={venues}
        initialVenueId={createModalDefaults.venueId}
        initialDate={createModalDefaults.date}
        initialTime={createModalDefaults.time}
        initialCustomerName={createModalDefaults.customerName}
        initialCustomerPhone={createModalDefaults.customerPhone}
        initialNotes={createModalDefaults.notes}
        initialBookingChannel={createModalDefaults.bookingChannel}
      />
    </div>
  );
};

export default ReservationGridPage;
